import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon } from 'lucide-react';
import { Hero } from './components/Hero';
import { Advantages } from './components/Advantages';
import { PricingPanel } from './components/PricingPanel';
import { InstagramFeed } from './components/InstagramFeed';
import { LocationMap } from './components/LocationMap';
import { ContactSection } from './components/ContactSection';
import { BookingModal } from './components/BookingModal';
import { ThemeSwitcher } from './components/ThemeSwitcher';
import { QRCodeBlock } from './components/QRCodeBlock';
import { DemoPopup } from './components/DemoPopup';
import { Language, translations } from './utils/translations';
export function App() {
  const [theme, setTheme] = useState('gold');
  const [mode, setMode] = useState('dark');
  const [lang, setLang] = useState<Language>('en');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScrolledPast40, setIsScrolledPast40] = useState(false);
  const [isScrolledPast65, setIsScrolledPast65] = useState(false);
  const [isDemoFormOpen, setIsDemoFormOpen] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const themeParam = params.get('theme') || 'gold';
    const modeParam = params.get('mode') || 'dark';
    const langParam = params.get('lang') as Language || 'en';
    setTheme(themeParam);
    setMode(modeParam);
    if (['en', 'uk', 'ru'].includes(langParam)) {
      setLang(langParam);
    }
    document.documentElement.setAttribute('data-theme', themeParam);
    document.documentElement.setAttribute('data-mode', modeParam);
    document.documentElement.setAttribute('lang', langParam);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      if (docHeight > winHeight) {
        const percent = scrollTop / (docHeight - winHeight);
        setIsScrolledPast40(percent > 0.4);
        setIsScrolledPast65(percent > 0.65);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const updateUrlAndState = (newTheme: string, newMode: string, newLang: Language) => {
    setTheme(newTheme);
    setMode(newMode);
    setLang(newLang);
    document.documentElement.setAttribute('data-theme', newTheme);
    document.documentElement.setAttribute('data-mode', newMode);
    document.documentElement.setAttribute('lang', newLang);
    const url = new URL(window.location.href);
    url.searchParams.set('theme', newTheme);
    url.searchParams.set('mode', newMode);
    url.searchParams.set('lang', newLang);
    window.history.pushState({}, '', url);
  };
  const t = translations[lang];
  return <div className="min-h-screen w-full bg-zinc-950 md:bg-zinc-900 px-0 py-0 md:px-6 md:py-12 overflow-x-hidden flex flex-col justify-center items-center" data-id="element-0" data-mp="2swbuhhnohmnu">
      <ThemeSwitcher currentTheme={theme} currentMode={mode} currentLang={lang} onSelectTheme={t => updateUrlAndState(t, mode, lang)} onSelectMode={m => updateUrlAndState(theme, m, lang)} onSelectLang={l => updateUrlAndState(theme, mode, l)} isVisible={isScrolledPast40} isDemoPopupVisible={isScrolledPast65} isDemoFormOpen={isDemoFormOpen} data-id="element-1" data-mp="18boi2fqz5zts" />

      <main className="mx-auto min-h-screen w-full max-w-[480px] bg-zinc-950 px-5 pb-52 pt-5 md:min-h-[800px] md:rounded-[40px] md:bg-zinc-950 md:px-6 md:pt-6 md:pb-52 relative md:shadow-2xl md:border md:border-zinc-800" data-id="element-2" data-mp="1w33nwihymz9s">
        
        <motion.div initial="hidden" animate="visible" variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.08
          }
        }
      }} data-id="element-3" data-mp="1m44si1m7h04q">
          <motion.div variants={{
          hidden: {
            y: 12,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1
          }
        }} data-id="element-4" data-mp="qz8ghb9gdxsx">
            <Hero lang={lang} data-id="element-5" data-mp="h8drhz4tok3s" />
          </motion.div>
          <motion.div className="mt-8" variants={{
          hidden: {
            y: 12,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1
          }
        }} data-id="element-6" data-mp="c89b093wzpdk">
            <Advantages lang={lang} data-id="element-7" data-mp="19tnf2zrurog8" />
          </motion.div>
          <motion.div className="mt-6" variants={{
          hidden: {
            y: 12,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1
          }
        }} data-id="element-8" data-mp="14y836ddeo2mg">
            <PricingPanel lang={lang} data-id="element-9" data-mp="vv724r3ed1pn" />
          </motion.div>
          <motion.div className="mt-8" variants={{
          hidden: {
            y: 12,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1
          }
        }} data-id="element-10" data-mp="3knt14z9agroe">
            <InstagramFeed lang={lang} data-id="element-11" data-mp="1w4f4urx4rjp6" />
          </motion.div>
          <motion.div className="mt-8" variants={{
          hidden: {
            y: 12,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1
          }
        }} data-id="element-12" data-mp="1w69p040kv7s7">
            <LocationMap lang={lang} data-id="element-13" data-mp="253k1ttt1n4z8" />
          </motion.div>
          <motion.div className="mt-8" variants={{
          hidden: {
            y: 12,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1
          }
        }} data-id="element-14" data-mp="29wd7cjwc6hcd">
            <ContactSection lang={lang} data-id="element-15" data-mp="2jthtwku3getp" />
          </motion.div>
          <motion.div className="mt-8" variants={{
          hidden: {
            y: 12,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1
          }
        }} data-id="element-16" data-mp="2jw3yqcplog88">
            <QRCodeBlock theme={theme} mode={mode} lang={lang} data-id="element-17" data-mp="2stwefhz8hpw5" />
          </motion.div>
          <motion.footer className="mt-10 text-center text-[14px] font-medium text-zinc-500" variants={{
          hidden: {
            opacity: 0
          },
          visible: {
            opacity: 1
          }
        }} data-id="element-18" data-mp="tj4t9s1adb27">
            {t.footerText}{' '}
            <a href="https://linkery.art/" target="_blank" rel="noopener noreferrer" className="font-bold text-zinc-400 hover:text-gold-500 transition-colors" data-id="element-19" data-mp="1wt97xj1uu3tj">
              Linkery
            </a>
          </motion.footer>
        </motion.div>
      </main>

      {/* Mobile & Desktop Fixed Button */}
      <div className={`fixed inset-x-0 bottom-0 z-40 flex justify-center pointer-events-none md:bottom-12 transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${isScrolledPast65 ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`} data-id="element-20" data-mp="2kpmxtj2pz0xf">
        <div className="w-full max-w-[480px] flex flex-col items-center pointer-events-auto px-4 pb-4 md:px-8 md:pb-0" data-id="element-21" data-mp="2tkku9052svti">
          <div className="w-full border-t border-zinc-800 bg-zinc-950 p-4 backdrop-blur md:rounded-[24px] md:border md:shadow-2xl" data-id="element-22" data-mp="33go4yudzu2s2">
            <button onClick={() => setIsModalOpen(true)} type="button" className="flex w-full items-center justify-center gap-2 rounded-xl bg-gold-500 py-4 text-[15px] font-extrabold text-zinc-950 shadow-lg transition-transform active:scale-[0.98] hover:bg-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-zinc-950" data-id="element-23" data-mp="2yj5vn2w2dljj">
              <CalendarIcon className="h-5 w-5" aria-hidden="true" data-id="element-24" data-mp="3d2p6fqllmyuq" />
              {t.bookOnlineBtn}
            </button>
          </div>
        </div>
      </div>

      <DemoPopup isVisibleTrigger={isScrolledPast65} onFormOpenChange={setIsDemoFormOpen} lang={lang} data-id="element-25" data-mp="3443sk8r2o80a" />

      <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} theme={theme} mode={mode} lang={lang} data-id="element-26" data-mp="2pg0gqiqcm23k" />
    </div>;
}